import React, { useState, useEffect } from 'react'
import { CreditCard, Lock, Shield, CheckCircle, AlertCircle, DollarSign, Calendar, Clock } from 'lucide-react'
import { GuruConnectApi } from '../../services/guruConnectApi'

interface PaymentIntegrationProps {
  sessionId?: string
  contentId?: string
  amount: number
  currency?: string
  description: string
  onSuccess: (paymentId: string) => void
  onError: (error: string) => void
  onCancel: () => void
}

interface PaymentMethod {
  id: string
  type: 'card' | 'bank_account'
  last4: string
  brand?: string
  exp_month?: number
  exp_year?: number
  is_default: boolean
}

interface PaymentIntent {
  id: string
  amount: number
  currency: string
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'requires_capture' | 'canceled' | 'succeeded'
  client_secret: string
  payment_method_types: string[]
}

export const PaymentIntegration: React.FC<PaymentIntegrationProps> = ({
  sessionId,
  contentId,
  amount,
  currency = 'usd',
  description,
  onSuccess,
  onError,
  onCancel
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')
  const [showAddCard, setShowAddCard] = useState(false)
  
  // New card form
  const [cardNumber, setCardNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [cvv, setCvv] = useState('')
  const [cardholderName, setCardholderName] = useState('')
  const [saveCard, setSaveCard] = useState(true)

  useEffect(() => {
    loadPaymentMethods()
    createPaymentIntent()
  }, [])

  const loadPaymentMethods = async () => {
    try {
      // In a real app, you would load saved payment methods from your backend
      const methods = await GuruConnectApi.getPaymentMethods()
      setPaymentMethods(methods)
      if (methods.length > 0) {
        const defaultMethod = methods.find(m => m.is_default) || methods[0]
        setSelectedPaymentMethod(defaultMethod.id)
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err)
    }
  }

  const createPaymentIntent = async () => {
    try {
      setLoading(true)
      setError('')
      
      const intentData = {
        amount: Math.round(amount * 100), // Convert to cents
        currency,
        description,
        metadata: {
          session_id: sessionId,
          content_id: contentId,
          type: sessionId ? 'session' : 'content'
        }
      }

      const intent = await GuruConnectApi.createPaymentIntent(intentData)
      setPaymentIntent(intent)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create payment intent'
      setError(errorMessage)
      onError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const addPaymentMethod = async () => {
    if (!cardNumber || !expiryDate || !cvv || !cardholderName) {
      setError('Please fill in all card details')
      return
    }

    try {
      setLoading(true)
      setError('')
      
      const [expMonth, expYear] = expiryDate.split('/')
      const paymentMethodData = {
        type: 'card',
        card: {
          number: cardNumber.replace(/\s/g, ''),
          exp_month: parseInt(expMonth),
          exp_year: parseInt(expYear),
          cvc: cvv
        },
        billing_details: {
          name: cardholderName
        }
      }

      const newMethod = await GuruConnectApi.addPaymentMethod(paymentMethodData)
      setPaymentMethods(prev => [...prev, newMethod])
      setSelectedPaymentMethod(newMethod.id)
      setShowAddCard(false)
      
      // Reset form
      setCardNumber('')
      setExpiryDate('')
      setCvv('')
      setCardholderName('')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add payment method'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const processPayment = async () => {
    if (!paymentIntent || !selectedPaymentMethod) {
      setError('Please select a payment method')
      return
    }

    try {
      setProcessing(true)
      setError('')
      
      const paymentData = {
        payment_intent_id: paymentIntent.id,
        payment_method_id: selectedPaymentMethod
      }

      const result = await GuruConnectApi.confirmPayment(paymentData)
      
      if (result.status === 'succeeded') {
        onSuccess(result.id)
      } else {
        setError('Payment processing failed. Please try again.')
        onError('Payment processing failed')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed'
      setError(errorMessage)
      onError(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
    const matches = v.match(/\d{4,16}/g)
    const match = matches && matches[0] || ''
    const parts = []
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4))
    }
    
    if (parts.length) {
      return parts.join(' ')
    } else {
      return v
    }
  }

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4)
    }
    return v
  }

  const getCardBrand = (number: string) => {
    const cleanNumber = number.replace(/\s/g, '')
    if (/^4/.test(cleanNumber)) return 'visa'
    if (/^5[1-5]/.test(cleanNumber)) return 'mastercard'
    if (/^3[47]/.test(cleanNumber)) return 'amex'
    if (/^6/.test(cleanNumber)) return 'discover'
    return 'unknown'
  }

  const getCardIcon = (brand: string) => {
    switch (brand) {
      case 'visa':
        return '💳'
      case 'mastercard':
        return '💳'
      case 'amex':
        return '💳'
      case 'discover':
        return '💳'
      default:
        return '💳'
    }
  }

  if (loading && !paymentIntent) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Preparing payment...</span>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Complete Payment</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mt-1">{description}</p>
      </div>

      {/* Payment Amount */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-lg font-medium text-gray-900 dark:text-white">Total Amount</span>
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-green-600">
              {amount.toFixed(2)}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 uppercase">
              {currency}
            </span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Payment Methods */}
      <div className="p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Payment Method</h3>
        
        {paymentMethods.length > 0 && (
          <div className="space-y-3 mb-4">
            {paymentMethods.map((method) => (
              <label
                key={method.id}
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedPaymentMethod === method.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value={method.id}
                  checked={selectedPaymentMethod === method.id}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {method.brand ? `${method.brand.charAt(0).toUpperCase() + method.brand.slice(1)}` : 'Card'} •••• {method.last4}
                    </p>
                    {method.exp_month && method.exp_year && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Expires {method.exp_month}/{method.exp_year}
                      </p>
                    )}
                  </div>
                  {method.is_default && (
                    <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                      Default
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {/* Add New Card */}
        {showAddCard ? (
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Add New Card</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Card Number
                </label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expiry Date
                  </label>
                  <input
                    type="text"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                    placeholder="MM/YY"
                    maxLength={5}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                    placeholder="123"
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cardholder Name
                </label>
                <input
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Save this card for future payments
                </span>
              </label>
            </div>
            
            <div className="flex space-x-3 mt-4">
              <button
                onClick={addPaymentMethod}
                disabled={loading || !cardNumber || !expiryDate || !cvv || !cardholderName}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                  loading || !cardNumber || !expiryDate || !cvv || !cardholderName
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? 'Adding...' : 'Add Card'}
              </button>
              <button
                onClick={() => setShowAddCard(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddCard(true)}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            + Add New Payment Method
          </button>
        )}
      </div>

      {/* Security Notice */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <Lock className="w-4 h-4" />
          <span>Your payment information is secure and encrypted</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex space-x-3">
          <button
            onClick={processPayment}
            disabled={processing || !selectedPaymentMethod}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-colors ${
              processing || !selectedPaymentMethod
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {processing ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </div>
            ) : (
              `Pay ${amount.toFixed(2)} ${currency.toUpperCase()}`
            )}
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
          By completing this payment, you agree to our terms of service and privacy policy
        </p>
      </div>
    </div>
  )
} 